import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { useClientPortal } from '../contexts/ClientPortalContext';
import { toast } from 'sonner';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Building2,
  Download,
  Minus,
  ArrowUpRight,
  ArrowDownRight,
  CheckCircle2,
  XCircle,
  AlertCircle,
  FileText,
  Eye,
  Search,
  List,
  Grid,
  ClipboardList,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ComposedChart, ReferenceLine,
} from 'recharts';
import api from '../services/api';
import { clientActionPlanService } from '../services';

const ABBEY_GREEN = '#1a4d2e';
const ABBEY_GOLD = '#c9a84c';
const STATUS_COLORS = { green: '#22c55e', amber: '#f59e0b', red: '#ef4444', neutral: '#94a3b8' };

const formatVal = (v, fmt) => {
  if (v == null) return 'N/A';
  if (fmt === 'percent') return `${Number(v).toFixed(1)}%`;
  if (fmt === 'days') return `${Math.round(v)} days`;
  if (fmt === 'currency') {
    const n = Number(v);
    const abs = Math.abs(n);
    const s = abs >= 1000 ? `£${(abs / 1000).toFixed(1)}k` : `£${Math.round(abs).toLocaleString()}`;
    return n < 0 ? `(${s})` : s;
  }
  return String(v);
};

const formatChange = (change, fmt) => {
  if (change == null) return null;
  const n = Number(change);
  if (fmt === 'percent') return `${n > 0 ? '+' : ''}${n.toFixed(1)}%`;
  if (fmt === 'days') return `${n > 0 ? '+' : ''}${Math.round(n)}`;
  if (fmt === 'currency') {
    const abs = Math.abs(n);
    const s = abs >= 1000 ? `£${(abs / 1000).toFixed(1)}k` : `£${Math.round(abs)}`;
    return n < 0 ? `(${s})` : `+${s}`;
  }
  return String(change);
};

function KPITile({ kpi, auditDate, tooltip }) {
  const statusColor = STATUS_COLORS[kpi.status] || STATUS_COLORS.neutral;
  const changeStr = formatChange(kpi.change, kpi.format);
  const isPositive = kpi.change != null && kpi.change > 0;
  const isNegative = kpi.change != null && kpi.change < 0;

  return (
    <Card className="relative overflow-hidden" data-testid={`kpi-tile-${kpi.label.replace(/\s/g, '-').toLowerCase()}`}
      title={tooltip || ''}>
      <div className="absolute top-0 left-0 w-1 h-full" style={{ background: statusColor }} />
      <CardContent className="p-4 pl-5">
        <p className="text-xs text-muted-foreground mb-1 truncate">{kpi.label}</p>
        <p className="text-xl font-bold" style={{ color: ABBEY_GREEN }}>
          {formatVal(kpi.value, kpi.format)}
        </p>
        {kpi.variance != null && (
          <p className="text-xs text-muted-foreground">
            {kpi.format === 'days' ? `Target: ${kpi.target} days` : `Budget: ${kpi.budget}%`}
            <span className="ml-1 font-medium" style={{ color: statusColor }}>
              ({kpi.variance > 0 ? '+' : ''}{kpi.variance}{kpi.format === 'percent' ? '%' : ''})
            </span>
          </p>
        )}
        {changeStr && (
          <div className="flex items-center gap-1 mt-1">
            {isPositive && <ArrowUpRight className="h-3 w-3" style={{ color: statusColor }} />}
            {isNegative && <ArrowDownRight className="h-3 w-3" style={{ color: statusColor }} />}
            {!isPositive && !isNegative && <Minus className="h-3 w-3 text-muted-foreground" />}
            <span className="text-xs" style={{ color: statusColor }}>
              {changeStr} vs last audit
            </span>
          </div>
        )}
        {auditDate && (
          <p className="text-[10px] text-muted-foreground mt-1.5">
            As at {new Date(auditDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function HealthScorecard({ scorecard }) {
  const icons = {
    green: <CheckCircle2 className="h-4 w-4" style={{ color: STATUS_COLORS.green }} />,
    amber: <AlertCircle className="h-4 w-4" style={{ color: STATUS_COLORS.amber }} />,
    red: <XCircle className="h-4 w-4" style={{ color: STATUS_COLORS.red }} />,
    neutral: <Minus className="h-4 w-4" style={{ color: STATUS_COLORS.neutral }} />,
  };

  const bgColors = {
    green: 'bg-green-50 border-green-200',
    amber: 'bg-amber-50 border-amber-200',
    red: 'bg-red-50 border-red-200',
    neutral: 'bg-slate-50 border-slate-200',
  };

  const labels = {
    green: 'Favourable',
    amber: 'Caution',
    red: 'Unfavourable',
    neutral: 'N/A',
  };

  return (
    <div className="space-y-2" data-testid="health-scorecard">
      {scorecard.map((item, idx) => (
        <div key={idx} className={`flex items-center justify-between py-2 px-3 rounded-md border ${bgColors[item.status] || bgColors.neutral}`}
          title={item.tooltip || ''}>
          <div className="flex-1 min-w-0">
            <span className="text-sm font-medium">{item.metric}</span>
            {item.value && (
              <span className="text-xs text-muted-foreground ml-2">({item.value})</span>
            )}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <span className="w-3 h-3 rounded-full" style={{ backgroundColor: STATUS_COLORS[item.status] || STATUS_COLORS.neutral }} />
            {icons[item.status]}
            <span className="text-xs font-semibold" style={{ color: STATUS_COLORS[item.status] }}>
              {labels[item.status] || 'N/A'}
            </span>
          </div>
        </div>
      ))}
      <p className="text-[10px] text-muted-foreground italic mt-2">
        Traffic light indicators are based on organisation threshold settings. Custom client thresholds applied where configured.
      </p>
    </div>
  );
}

// Trend Comparison Component - side-by-side KPI comparison between two audit periods
function TrendComparison({ trendData }) {
  const [periodA, setPeriodA] = React.useState(trendData.length - 1); // Latest
  const [periodB, setPeriodB] = React.useState(trendData.length >= 2 ? trendData.length - 2 : 0); // Previous

  const a = trendData[periodA] || {};
  const b = trendData[periodB] || {};

  const metrics = [
    { label: 'Stock Result', key: 'stocktake_result_percent', key2: 'stocktake_result_value', fmt: 'percent', fmtAlt: 'currency', closerToZero: true },
    { label: 'COS %', key: 'cos_percent', fmt: 'percent', lower: true },
    { label: 'GP %', key: 'gp_percent', fmt: 'percent', lower: false },
    { label: 'Stockholding Days', key: 'stockholding_days', fmt: 'days', lower: true },
    { label: 'Stockholding Value', key: 'stockholding_value', fmt: 'currency', lower: true },
    { label: 'Wastage', key: 'wastage_value', fmt: 'currency', lower: true },
    { label: 'Sales', key: 'sales_for_period', fmt: 'currency', lower: false },
  ];

  const fmtVal = (v, fmt) => {
    if (v == null || isNaN(Number(v))) return '-';
    const n = Number(v);
    if (fmt === 'percent') return `${n.toFixed(1)}%`;
    if (fmt === 'days') return `${Math.round(n)} days`;
    if (fmt === 'currency') return n < 0 ? `£(${Math.abs(Math.round(n)).toLocaleString()})` : `£${Math.round(n).toLocaleString()}`;
    return String(n);
  };

  const getChange = (aVal, bVal, lowerBetter, closerToZero) => {
    const na = Number(aVal), nb = Number(bVal);
    if (isNaN(na) || isNaN(nb)) return { icon: null, color: 'text-slate-400', bg: '' };
    const diff = na - nb;
    if (Math.abs(diff) < 0.001) return { icon: <Minus className="h-3 w-3" />, color: 'text-slate-400', bg: '' };
    let improved;
    if (closerToZero) {
      improved = Math.abs(na) < Math.abs(nb);
    } else {
      improved = lowerBetter ? diff < 0 : diff > 0;
    }
    return {
      icon: improved ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />,
      color: improved ? 'text-green-700' : 'text-red-700',
      bg: improved ? 'bg-green-50' : 'bg-red-50',
      label: improved ? 'Improved' : 'Declined'
    };
  };

  return (
    <Card data-testid="trend-comparison">
      <CardHeader className="pb-2">
        <CardTitle className="text-sm font-medium">Period Comparison</CardTitle>
        <p className="text-xs text-muted-foreground">Compare KPIs between two audit periods</p>
      </CardHeader>
      <CardContent>
        <div className="flex gap-4 mb-3">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground block mb-1">Period A</label>
            <Select value={String(periodA)} onValueChange={v => setPeriodA(Number(v))}>
              <SelectTrigger className="h-8 text-xs" data-testid="period-a-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {trendData.map((d, i) => (
                  <SelectItem key={i} value={String(i)}>{d.date}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground block mb-1">Period B</label>
            <Select value={String(periodB)} onValueChange={v => setPeriodB(Number(v))}>
              <SelectTrigger className="h-8 text-xs" data-testid="period-b-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {trendData.map((d, i) => (
                  <SelectItem key={i} value={String(i)}>{d.date}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="text-left py-1.5 font-medium text-slate-600">Metric</th>
              <th className="text-center py-1.5 font-medium text-slate-600">{a.date || '-'}</th>
              <th className="text-center py-1.5 font-medium text-slate-600">{b.date || '-'}</th>
              <th className="text-center py-1.5 font-medium text-slate-600">Change</th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((m, i) => {
              const change = getChange(a[m.key], b[m.key], m.lower, m.closerToZero);
              return (
                <tr key={i} className={`border-b border-slate-100 ${change.bg}`}>
                  <td className="py-1.5 font-medium">{m.label}</td>
                  <td className="py-1.5 text-center">{fmtVal(a[m.key], m.fmt)}</td>
                  <td className="py-1.5 text-center">{fmtVal(b[m.key], m.fmt)}</td>
                  <td className="py-1.5 text-center">
                    <span className={`inline-flex items-center gap-0.5 ${change.color}`}>
                      {change.icon}
                      {change.label && <span className="text-[10px]">{change.label}</span>}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}

const ClientPropertyDashboardPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { isAnonymised, anonymise } = useImpersonation();
  const { apEnabled } = useClientPortal();
  const [sites, setSites] = useState([]);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dashLoading, setDashLoading] = useState(false);
  const [siteSearch, setSiteSearch] = useState('');
  const [siteViewMode, setSiteViewMode] = useState('list'); // 'list' or 'dropdown'
  const [apSummary, setApSummary] = useState(null);
  const [apLoading, setApLoading] = useState(false);

  // Check for site parameter in URL (for drill-down from portfolio)
  useEffect(() => {
    const fetchSites = async () => {
      try {
        const resp = await api.get('/client/reports/sites');
        const s = resp.data || [];
        setSites(s.map(item => ({ id: item.site_id, name: item.site_name, group_name: item.group_name })));
        
        // Check URL for site parameter
        const urlParams = new URLSearchParams(window.location.search);
        const siteParam = urlParams.get('site');
        
        if (siteParam) {
          const siteId = parseInt(siteParam, 10);
          if (s.some(site => site.site_id === siteId)) {
            setSelectedSiteId(siteId);
          } else if (s.length === 1) {
            setSelectedSiteId(s[0].site_id);
          }
        } else if (s.length === 1) {
          setSelectedSiteId(s[0].site_id);
        }
        
        // Use list view for multi-site users by default
        if (s.length > 5) {
          setSiteViewMode('list');
        } else {
          setSiteViewMode('dropdown');
        }
      } catch (e) {
        console.error('Failed to fetch sites:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchSites();
  }, []);

  // Filter sites by search
  const filteredSites = useMemo(() => {
    if (!siteSearch) return sites;
    const search = siteSearch.toLowerCase();
    return sites.filter(
      s => s.name.toLowerCase().includes(search) ||
           (s.group_name && s.group_name.toLowerCase().includes(search))
    );
  }, [sites, siteSearch]);

  useEffect(() => {
    if (!selectedSiteId) return;
    const fetchDashboard = async () => {
      setDashLoading(true);
      try {
        const [dashResp, reportsResp] = await Promise.all([
          api.get(`/client/dashboard/site/${selectedSiteId}`),
          api.get(`/client/summary-reports/site/${selectedSiteId}`),
        ]);
        setDashboardData(dashResp.data);
        setReports(reportsResp.data || []);
      } catch (e) {
        console.error('Failed to fetch dashboard:', e);
        setDashboardData(null);
      } finally {
        setDashLoading(false);
      }
    };
    fetchDashboard();
  }, [selectedSiteId]);

  // Action Plan oversight — only fetch when AP is enabled for this customer's scope.
  useEffect(() => {
    if (!apEnabled || !selectedSiteId) { setApSummary(null); return; }
    let cancelled = false;
    setApLoading(true);
    clientActionPlanService.analytics({ site_id: selectedSiteId })
      .then(res => { if (!cancelled) setApSummary(res.summary); })
      .catch(() => { if (!cancelled) setApSummary(null); })
      .finally(() => { if (!cancelled) setApLoading(false); });
    return () => { cancelled = true; };
  }, [selectedSiteId, apEnabled]);

  const [downloadingPdfId, setDownloadingPdfId] = useState(null);

  const handleDownloadPdf = async (reportId) => {
    setDownloadingPdfId(reportId);
    toast.info('Generating PDF...', { id: `pdf-${reportId}`, duration: 30000 });
    try {
      const resp = await api.get(`/client/summary-reports/${reportId}/pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([resp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `Summary_Report_${reportId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded', { id: `pdf-${reportId}` });
    } catch (e) {
      console.error('Failed to download PDF:', e);
      toast.error('PDF download failed', { id: `pdf-${reportId}` });
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const trendData = useMemo(() => {
    if (!dashboardData?.trend_data) return [];
    return dashboardData.trend_data.map(d => ({
      ...d,
      date: d.audit_date ? new Date(d.audit_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: '2-digit' }) : '',
    }));
  }, [dashboardData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: ABBEY_GREEN }} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="client-property-dashboard">
      {/* Site Selector - Enhanced for Multi-Site */}
      {sites.length > 1 && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-medium flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-600" />
                Select Property
              </CardTitle>
              {sites.length > 5 && (
                <div className="flex items-center gap-1 border rounded-md p-0.5">
                  <Button
                    variant={siteViewMode === 'list' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setSiteViewMode('list')}
                    data-testid="view-list-btn"
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    variant={siteViewMode === 'dropdown' ? 'secondary' : 'ghost'}
                    size="sm"
                    className="h-7 px-2"
                    onClick={() => setSiteViewMode('dropdown')}
                    data-testid="view-dropdown-btn"
                  >
                    <Grid className="h-4 w-4" />
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="pt-0">
            {/* Dropdown Mode */}
            {siteViewMode === 'dropdown' && (
              <div className="max-w-sm">
                <Select
                  value={selectedSiteId ? String(selectedSiteId) : ''}
                  onValueChange={(v) => setSelectedSiteId(Number(v))}
                >
                  <SelectTrigger data-testid="site-selector">
                    <SelectValue placeholder="Select a property..." />
                  </SelectTrigger>
                  <SelectContent>
                    {sites.map(s => (
                      <SelectItem key={s.id} value={String(s.id)}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* List Mode - Searchable */}
            {siteViewMode === 'list' && (
              <div className="space-y-3">
                <div className="relative max-w-sm">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search properties..."
                    value={siteSearch}
                    onChange={(e) => setSiteSearch(e.target.value)}
                    className="pl-9 h-9"
                    data-testid="site-search-input"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
                  {filteredSites.map(s => (
                    <button
                      key={s.id}
                      onClick={() => setSelectedSiteId(s.id)}
                      className={`text-left p-3 rounded-lg border transition-colors ${
                        selectedSiteId === s.id
                          ? 'bg-emerald-50 border-emerald-300'
                          : 'bg-white border-slate-200 hover:border-emerald-200 hover:bg-slate-50'
                      }`}
                      data-testid={`site-option-${s.id}`}
                    >
                      <p className={`text-sm font-medium ${selectedSiteId === s.id ? 'text-emerald-700' : 'text-slate-900'}`}>
                        {anonymise(s.name, 'site')}
                      </p>
                      {s.group_name && (
                        <p className="text-xs text-muted-foreground truncate">{anonymise(s.group_name, 'group')}</p>
                      )}
                    </button>
                  ))}
                  {filteredSites.length === 0 && (
                    <p className="text-sm text-muted-foreground col-span-full text-center py-4">
                      No properties match your search
                    </p>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {!selectedSiteId && sites.length > 1 && (
        <Card>
          <CardContent className="p-8 text-center text-muted-foreground">
            <Building2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p>Select a property to view the dashboard</p>
          </CardContent>
        </Card>
      )}

      {/* Action Plan — leaf of the client hierarchy (portfolio/region → site
      {/* Action Plan card — only shown when AP is enabled for this customer. */}
      {apEnabled === true && selectedSiteId && (
        <Card data-testid="property-ap-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-medium flex items-center gap-2">
              <ClipboardList className="h-4 w-4" style={{ color: ABBEY_GREEN }} />
              Action Plan
            </CardTitle>
          </CardHeader>
          <CardContent>
            {apLoading ? (
              <div className="flex justify-center py-4 text-slate-400">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: ABBEY_GREEN }} />
              </div>
            ) : !apSummary ? (
              <p className="text-sm text-muted-foreground text-center py-3">Action plan data unavailable.</p>
            ) : apSummary.total_canonical_actions === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-3">No action plan items for this property.</p>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {[
                    { key: 'outstanding', label: 'Outstanding', value: apSummary.outstanding },
                    { key: 'overdue', label: 'Overdue', value: apSummary.overdue, highlight: apSummary.overdue > 0 },
                    { key: 'awaiting_verification', label: 'Pending Verification', value: apSummary.awaiting_verification },
                    { key: 'verified', label: 'Verified (30d)', value: apSummary.verified_closed_last_30d },
                  ].map(tile => (
                    <div
                      key={tile.key}
                      onClick={() => navigate(`/client/action-plans/analytics?site_id=${selectedSiteId}&state=${tile.key}&from=property`)}
                      className={`rounded-lg border p-3 flex flex-col gap-0.5 cursor-pointer hover:border-emerald-300 transition-colors ${tile.highlight ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-white'}`}
                      data-testid={`property-ap-tile-${tile.key}`}
                    >
                      <span className="text-xs text-muted-foreground">{tile.label}</span>
                      <span className="text-xl font-bold" style={{ color: tile.highlight ? STATUS_COLORS.red : ABBEY_GREEN }}>{tile.value ?? '—'}</span>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end mt-3">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => navigate(`/client/action-plans?site_id=${selectedSiteId}`)}
                    data-testid="property-ap-view-btn"
                  >
                    View Action Plan{apSummary.total_canonical_actions !== 1 ? 's' : ''} <Eye className="h-3.5 w-3.5 ml-1" />
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {dashLoading && (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: ABBEY_GREEN }} />
        </div>
      )}

      {!dashLoading && dashboardData && !dashboardData.has_data && (
        <Card data-testid="no-data-message">
          <CardContent className="p-8 text-center text-muted-foreground">
            <BarChart3 className="h-10 w-10 mx-auto mb-3 opacity-30" />
            <p className="font-medium">No audit data available yet</p>
            <p className="text-sm mt-1">Published audit reports for {anonymise(dashboardData.site_name, 'site')} will appear here.</p>
          </CardContent>
        </Card>
      )}

      {!dashLoading && dashboardData?.has_data && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold" style={{ color: ABBEY_GREEN }}>
                {anonymise(dashboardData.site_name, 'site')}
              </h2>
              <p className="text-sm text-muted-foreground">
                {dashboardData.total_audits} published audit{dashboardData.total_audits !== 1 ? 's' : ''}
                {' '}&middot; Latest: {new Date(dashboardData.latest_audit_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
              </p>
            </div>
          </div>

          {/* KPI Tiles */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kpi-tiles">
            {dashboardData.kpi_tiles.map((kpi, idx) => {
              const tooltips = [
                `Stock Result: GREEN if between ${dashboardData.threshold_info?.stock_result_green_lower ?? -2.0}% and ${dashboardData.threshold_info?.stock_result_green_upper ?? 0.5}%${dashboardData.threshold_info?.has_custom ? ' (custom criteria applied)' : ''}`,
                `COS vs Budget: GREEN if actual is at or below budget, AMBER if over by ≤${dashboardData.threshold_info?.cos_variance_amber ?? 0.5}%, RED if over by more`,
                'Stockholding Days: GREEN if at or below target, AMBER within 2 days, RED over 2 days',
                'Sales: GREEN if higher than previous period, RED if lower',
              ];
              return <KPITile key={idx} kpi={kpi} auditDate={kpi.audit_date || dashboardData.latest_audit_date} tooltip={tooltips[idx]} />;
            })}
          </div>

          {/* Charts Row 1 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Stock Result Trend (Amount + %) */}
            <Card data-testid="chart-stock-result">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Stock Result Trend</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis 
                      yAxisId="left" 
                      tick={{ fontSize: 10 }} 
                      tickFormatter={v => `£${Math.round(v).toLocaleString()}`}
                      label={{ value: 'Amount (£)', angle: -90, position: 'insideLeft', style: { fontSize: 10, fill: '#6b7280' } }}
                    />
                    <YAxis 
                      yAxisId="right" 
                      orientation="right" 
                      tick={{ fontSize: 10 }} 
                      tickFormatter={v => `${v}%`}
                      label={{ value: '%', angle: 90, position: 'insideRight', style: { fontSize: 10, fill: '#6b7280' } }}
                    />
                    <Tooltip 
                      formatter={(v, name) => {
                        if (name === 'Amount') return v != null ? `£${Math.round(v).toLocaleString()}` : 'N/A';
                        return v != null ? `${Number(v).toFixed(1)}%` : 'N/A';
                      }}
                    />
                    <Legend />
                    <ReferenceLine yAxisId="right" y={0} stroke="#94a3b8" strokeDasharray="3 3" />
                    <Bar 
                      yAxisId="left"
                      dataKey="stocktake_result_value"
                      fill={ABBEY_GREEN}
                      fillOpacity={0.3}
                      name="Amount"
                    />
                    <Line
                      yAxisId="right"
                      type="monotone"
                      dataKey="stocktake_result_percent"
                      stroke={ABBEY_GREEN}
                      strokeWidth={2}
                      dot={{ fill: ABBEY_GREEN, r: 4 }}
                      name="Result %"
                      connectNulls
                    />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Actual COS % */}
            <Card data-testid="chart-cos-budget">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Actual COS %</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis 
                      tick={{ fontSize: 11 }} 
                      tickFormatter={v => `${v}%`}
                      domain={[
                        (dataMin) => {
                          const budget = dashboardData?.budget_cos_pct;
                          const minVal = budget != null ? Math.min(dataMin, budget) : dataMin;
                          return Math.floor(minVal - 2);
                        },
                        (dataMax) => {
                          const budget = dashboardData?.budget_cos_pct;
                          const maxVal = budget != null ? Math.max(dataMax, budget) : dataMax;
                          return Math.ceil(maxVal + 2);
                        }
                      ]}
                    />
                    <Tooltip formatter={v => v != null ? `${v.toFixed(1)}%` : 'N/A'} />
                    {dashboardData.budget_cos_pct != null && (
                      <ReferenceLine
                        y={dashboardData.budget_cos_pct}
                        stroke={ABBEY_GOLD}
                        strokeDasharray="5 5"
                        label={{ value: `Budget ${dashboardData.budget_cos_pct}%`, position: 'right', fontSize: 10, fill: ABBEY_GOLD }}
                      />
                    )}
                    <Line
                      type="monotone"
                      dataKey="cos_percent"
                      stroke={ABBEY_GREEN}
                      strokeWidth={2}
                      dot={{ fill: ABBEY_GREEN, r: 4 }}
                      name="Actual COS %"
                      connectNulls
                    />
                    <Legend />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Charts Row 2 */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Stockholding Value & Days */}
            <Card data-testid="chart-stockholding">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Stockholding Value & Days</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={220}>
                  <ComposedChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={v => `£${(v / 1000).toFixed(0)}k`} />
                    <YAxis yAxisId="days" orientation="right" tick={{ fontSize: 11 }} tickFormatter={v => `${v}d`} />
                    <Tooltip formatter={(v, name) =>
                      name === 'Stockholding Value' ? `£${Number(v).toLocaleString()}` : `${v} days`
                    } />
                    {dashboardData.target_stockholding_days != null && (
                      <ReferenceLine
                        yAxisId="days"
                        y={dashboardData.target_stockholding_days}
                        stroke={ABBEY_GOLD}
                        strokeDasharray="5 5"
                        label={{ value: `Target ${dashboardData.target_stockholding_days}d`, position: 'right', fontSize: 10, fill: ABBEY_GOLD }}
                      />
                    )}
                    <Bar
                      yAxisId="value"
                      dataKey="stockholding_value"
                      fill={ABBEY_GREEN}
                      opacity={0.3}
                      name="Stockholding Value"
                      radius={[3, 3, 0, 0]}
                    />
                    <Line
                      yAxisId="days"
                      type="monotone"
                      dataKey="stockholding_days"
                      stroke={ABBEY_GOLD}
                      strokeWidth={2}
                      dot={{ fill: ABBEY_GOLD, r: 4 }}
                      name="Stockholding Days"
                      connectNulls
                    />
                    <Legend />
                  </ComposedChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* KPI Health Summary */}
            <Card data-testid="chart-kpi-health">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">KPI Health Summary</CardTitle>
                <p className="text-xs text-muted-foreground">Latest audit scorecard</p>
              </CardHeader>
              <CardContent>
                <HealthScorecard scorecard={dashboardData.health_scorecard} />
              </CardContent>
            </Card>
          </div>

          {/* Trend Comparison */}
          {trendData.length >= 2 && (
            <TrendComparison trendData={trendData} />
          )}

          {/* Published Reports */}
          {reports.length > 0 && (
            <Card data-testid="published-reports-list">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Published Audit Reports</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {reports.map(r => (
                    <div key={r.id} className="flex items-center justify-between py-2 px-3 bg-slate-50 rounded-md">
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">{r.scope} Audit — {new Date(r.audit_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                          <p className="text-xs text-muted-foreground">Published {r.published_at ? new Date(r.published_at).toLocaleDateString('en-GB') : ''}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => navigate(`/client/report/${r.id}`)}
                          data-testid={`view-report-${r.id}`}
                        >
                          <Eye className="h-3.5 w-3.5 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDownloadPdf(r.id)}
                          disabled={downloadingPdfId === r.id || isAnonymised}
                          title={isAnonymised ? 'PDF download disabled in Anonymised Demo Mode' : undefined}
                          data-testid={`download-report-${r.id}`}
                        >
                          {downloadingPdfId === r.id ? (
                            <div className="h-3.5 w-3.5 mr-1 animate-spin border-2 border-current border-t-transparent rounded-full" />
                          ) : (
                            <Download className="h-3.5 w-3.5 mr-1" />
                          )}
                          {downloadingPdfId === r.id ? 'Generating...' : 'PDF'}
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default ClientPropertyDashboardPage;
